import { useMemo } from "react"
import { type UseQueryExecute, useQuery } from "urql"

import type { ProductQualityInstructions } from "../types"

import {
  GetProductQualityTestTemplateInstructionsDocument,
  type ProductGetFailure,
  type ProductGetSuccess,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGetProductQualityInstructionsReturn {
  productQualityInstructions: ProductQualityInstructions | undefined
  isFetching: boolean
  refetch: UseQueryExecute
}

export const useGetProductQualityInstructions = (productId: number): UseGetProductQualityInstructionsReturn => {
  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: GetProductQualityTestTemplateInstructionsDocument,
    variables: {
      productId,
    },
  })
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  const productQualityInstructions = useMemo(() => {
    if (!data) return undefined

    if (data.product.get.__typename === "ProductGetFailure" || error) {
      notify({
        message: "Failed to fetch product quality instructions",
        content: error?.message ?? (data.product.get as ProductGetFailure).error.message,
        type: NotificationType.Error,
      })
      return undefined
    }

    return (data.product.get as ProductGetSuccess).product
  }, [data, notify, error])

  return {
    productQualityInstructions,
    isFetching: fetching,
    refetch: reexecuteQuery,
  }
}
